import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Droplet } from 'lucide-react';
import { api } from '../lib/api';
import type { VerifyPinResponse } from '../../../shared/contracts';

export default function MaintenanceMode() {
  const navigate = useNavigate();
  const [pinCode, setPinCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pinCode.trim()) {
      setError('Please enter the PIN code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await api.post<VerifyPinResponse>('/app-settings/verify-pin', {
        pinCode,
        pinType: 'maintenance',
      });

      if ((response as VerifyPinResponse).success) {
        // Store that the user has verified the maintenance PIN in this session
        sessionStorage.setItem('maintenancePinEntered', 'true');
        // Navigate to the login page so the user can proceed normally
        navigate('/login');
      } else {
        setError((response as VerifyPinResponse).message || 'Invalid PIN code');
      }
    } catch (err: any) {
      console.error('[MaintenanceMode] Error verifying PIN:', err);
      setError('Failed to verify PIN. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-full p-4 shadow-lg">
              <Droplet className="h-12 w-12 text-cyan-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AquaFlow</h1>
          <p className="text-gray-600">Water Management Platform</p>
        </div>

        {/* Maintenance Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-yellow-100 rounded-full">
              <AlertCircle size={40} className="text-yellow-600" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Maintenance Mode
          </h2>

          <p className="text-center text-gray-600 mb-8">
            The application is currently in maintenance mode. Please enter the access PIN code to continue.
          </p>

          {/* Form */}
          <form onSubmit={handleVerifyPin} className="space-y-6">
            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                Access PIN Code
              </label>
              <input
                id="pin"
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={pinCode}
                onChange={(e) => {
                  setPinCode(e.target.value);
                  setError('');
                }}
                disabled={isLoading}
                placeholder="Enter PIN"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-cyan-500 text-center text-2xl font-bold tracking-widest disabled:bg-gray-100"
                autoFocus
              />
              {error && (
                <p className="mt-2 text-sm text-red-600 font-medium">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </>
              ) : (
                'Verify PIN'
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-6">
            If you don&apos;t have the PIN code, please contact your administrator.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          WEWEB LLC.
        </p>
      </div>
    </div>
  );
}
